/**
 * Studio Bundles hooks — #152.
 *
 * TanStack Query wrappers around /v1/bundles. The Studio Bundles surface
 * lets a tenant browse platform-published industry kits and their own
 * draft bundles, then install a published bundle into the workspace
 * (clones each item row into the tenant's scope).
 *
 * Endpoints:
 *   GET    /bundles                 — list visible bundles
 *   GET    /bundles/:id             — detail w/ items
 *   POST   /bundles                 — create draft
 *   PATCH  /bundles/:id             — edit draft
 *   POST   /bundles/:id/publish     — freeze manifest
 *   POST   /bundles/:id/install     — clone into requesting tenant
 *   DELETE /bundles/:id             — archive
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export type BundleEntityKind = 'tool' | 'skill' | 'prompt' | 'agent' | 'mcp' | 'kb';
export type BundleStatus = 'draft' | 'published' | 'archived';
export type BundleAuthor = 'platform' | 'tenant';
export type IndustryTag =
  | 'manufacturing'
  | 'retail'
  | 'healthcare'
  | 'energy'
  | 'finance'
  | 'general';

export interface BundleItem {
  id: string;
  bundleId: string;
  entityKind: BundleEntityKind;
  entityId: string;
  entityVersionSnapshot: unknown;
  sortOrder: number;
  createdAt: string;
}

export interface BundleRow {
  id: string;
  organizationId: string | null;
  author: BundleAuthor;
  isReadOnly: boolean;
  slug: string;
  name: string;
  description: string;
  industryTag: string;
  version: string;
  status: BundleStatus;
  manifest: unknown;
  manifestHash: string | null;
  signature: string | null;
  authorUserId: string | null;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  archivedAt: string | null;
}

export interface BundleDetail extends BundleRow {
  items: BundleItem[];
}

export interface BundleListResponse {
  items: BundleRow[];
  total: number;
}

export interface BundleDetailResponse {
  data: BundleDetail;
}

export interface InstallResult {
  bundleId: string;
  installed: number;
  skipped: number;
  deferred: number;
  details: Array<{
    entityKind: BundleEntityKind;
    entityId: string;
    status: 'installed' | 'skipped' | 'deferred';
    newId?: string;
    reason?: string;
  }>;
}

const KEYS = {
  list: ['studio-bundles'] as const,
  detail: (id: string) => ['studio-bundles', id] as const,
};

// ─── Reads ─────────────────────────────────────────────────────────────

export function useBundles(opts: {
  industry?: IndustryTag;
  status?: BundleStatus;
  includeArchived?: boolean;
} = {}) {
  const params = new URLSearchParams();
  if (opts.industry) params.set('industry', opts.industry);
  if (opts.status) params.set('status', opts.status);
  if (opts.includeArchived) params.set('includeArchived', 'true');
  const qs = params.toString();
  return useQuery<BundleListResponse, Error>({
    queryKey: [...KEYS.list, opts],
    queryFn: () => apiClient.get<BundleListResponse>(`/bundles${qs ? `?${qs}` : ''}`),
  });
}

export function useBundle(id: string | undefined) {
  return useQuery<BundleDetailResponse, Error>({
    queryKey: id ? KEYS.detail(id) : ['studio-bundles', 'detail', 'undefined'],
    queryFn: () => apiClient.get<BundleDetailResponse>(`/bundles/${id}`),
    enabled: !!id,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────

export interface CreateBundleInput {
  slug: string;
  name: string;
  description?: string;
  industryTag?: IndustryTag;
  version?: string;
  items?: Array<{
    entityKind: BundleEntityKind;
    entityId: string;
    sortOrder?: number;
  }>;
}

export function useCreateBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBundleInput) =>
      apiClient.post<BundleDetailResponse>('/bundles', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export interface UpdateBundleInput {
  name?: string;
  description?: string;
  industryTag?: IndustryTag;
  version?: string;
  items?: Array<{
    entityKind: BundleEntityKind;
    entityId: string;
    sortOrder?: number;
  }>;
}

export function useUpdateBundle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBundleInput) =>
      apiClient.patch<BundleDetailResponse>(`/bundles/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}

export function usePublishBundle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<BundleDetailResponse>(`/bundles/${id}/publish`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}

export function useInstallBundle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<InstallResult>(`/bundles/${id}/install`, {}),
    onSuccess: () => {
      // Installing creates new workspace tool/skill/prompt rows — bust
      // those caches so the relevant Studio surfaces pick up the new
      // entries immediately.
      qc.invalidateQueries({ queryKey: ['workspace-tools'] });
      qc.invalidateQueries({ queryKey: ['studio-skills'] });
      qc.invalidateQueries({ queryKey: ['prompt-templates'] });
    },
  });
}

export function useArchiveBundle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete(`/bundles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}
